"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import Image from "next/image";

import { getFeaturedTestimonials, Testimonial } from "@/lib/data/testimonials";
import { Typography } from "@/components/ui";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import { env } from "@/lib/environment";

interface TestimonialCarouselProps {
  testimonials?: Testimonial[];
  showPagination?: boolean;
  autoplay?: boolean;
  slidesPerView?: number;
}

export default function TestimonialCarousel({
  testimonials = getFeaturedTestimonials(),
  showPagination = true,
  autoplay = true,
  slidesPerView = 1,
}: TestimonialCarouselProps) {
  return (
    <div className="swiper-container relative">
      <Swiper
        modules={[Autoplay, Navigation, Pagination]}
        spaceBetween={3}
        slidesPerView={slidesPerView}
        breakpoints={{
          0: { slidesPerView: 1 }, // mobile
          768: { slidesPerView: 2 }, // tablet (md)
          1024: { slidesPerView: 1 }, // desktop (lg)
        }}
        autoHeight={true}
        autoplay={
          autoplay
            ? {
                delay: 5000,
                disableOnInteraction: false,
                waitForTransition: true,
              }
            : false
        }
        loop={true}
        pagination={
          showPagination
            ? {
                clickable: true,
                el: ".testimonial-swiper-pagination",
              }
            : false
        }
        speed={800}
        className="testimonial-swiper"
      >
        {testimonials.map(testimonial => (
          <SwiperSlide key={testimonial.id} className="p-1 md:p-4">
            <div className="w-full max-w-[927px] h-dvh max-h-[655px] lg:h-auto p-6 lg:p-0 mx-auto bg-white rounded-xl md:rounded-2xl overflow-clip mb-4 shadow-md md:shadow-[6px_4px_24px_0px_rgba(0,0,0,0.12)]">
              <div className="flex flex-col-reverse lg:flex-row h-full">
                {/* Content Section */}
                <div className="w-full md:w-full p-0 lg:p-8 lg:pl-12 lg:pt-12 flex-1 flex-col">
                  <div className="flex flex-col h-full justify-between">
                    {/* Industry Tag */}
                  <div className="mb-6 hidden md:flex">
                    <span className="section-label">
                      {testimonial.industry}
                    </span>
                  </div>

                  {/* Quote */}
                  <div className="mb-8 flex flex-wrap">
                    <Typography
                      variant="large"
                      as="blockquote"
                      className="text-text-primary text-base lg:text-lg font-normal leading-relaxed"
                    >
                      <Image
                        src={`${env.NEXT_PUBLIC_CDN_URL}/images/qoutes.kv2d5D2KqWSQ.png`}
                        alt="quote"
                        title="quote"
                        aria-hidden="true"
                        width={41}
                        height={27}
                        className="float-left w-8 md:w-10 mr-2 relative mt-0.5"
                        loading="lazy"
                        decoding="async"
                      />

                      {testimonial.quote}
                    </Typography>
                  </div>

                  {/* Attribution */}
                  <div className="mt-auto">
                    <Typography variant="body" as="p" className="text-primary font-normal">
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body" as="p" className="text-text-secondary/50 text-base">
                      {testimonial.title}, {testimonial.company}
                    </Typography>
                  </div>

                  </div>
                </div>

                {/* Image Section */}
                <div className="w-full lg:w-[382px] flex flex-col items-start">
                  <span className="inline-block md:hidden mb-6 bg-[#1D2C7F] text-white px-4 py-1 rounded-full text-xs font-medium">
                    {testimonial.industry}
                  </span>
                  <Image
                    src={testimonial.image}
                    alt={`${testimonial.name} testimonial`}
                    title={`${testimonial.name} testimonial`}
                    width={382}
                    height={440}
                    className=" hidden lg:block"
                    loading="lazy"
                    decoding="async"
                  />
                  <Image
                    src={testimonial.imageSmall}
                    alt={`${testimonial.name} testimonial`}
                    title={`${testimonial.name} testimonial`}
                    width={278}
                    height={162}
                    className="rounded-2xl block lg:hidden mb-6 lg:w-full lg:h-full"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination */}
      {showPagination && (
        <div className="testimonial-swiper-pagination text-center gap-2 !relative !-mt-1"></div>
      )}
    </div>
  );
}
